import {
	INumberProfile,
	INumberTroubleProfile,
	ITollFreeQuery
} from 'RtUi/app/rt800/Numbers/lib/resources/NumberResource';
import { tfnStatusToName, timestampToReadable } from 'RtUi/utils/maps';
import { FC } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { RtxTextInput } from 'RtUi/components/rtx/inputs/Text/RtxTextInput';
import { CollapsibleCard } from 'RtUi/components/ui/CollapsibleCard';
import { TollFreeNumbersUtils } from 'RtUi/app/rt800/Numbers/lib/utils/TollFreeNumbersUtils';

interface IAdminDataInfoProps {
	profile: INumberProfile;
	troubleProfile: INumberTroubleProfile | undefined;
	queryProfile: ITollFreeQuery | undefined;
}

export const AdminDataInfo: FC<
	React.PropsWithChildren<IAdminDataInfoProps>
> = ({ profile, troubleProfile, queryProfile }) => {
	let errorText: string | null = null;
	if (
		queryProfile &&
		(!queryProfile.contactName || queryProfile.contactName.trim() === '') &&
		queryProfile.canManageRespOrg
	) {
		errorText = 'The Contact Name is blank';
	}

	if (
		queryProfile &&
		(!queryProfile.contactNumber || queryProfile.contactNumber.trim() === '') &&
		queryProfile.canManageRespOrg
	) {
		if (errorText) {
			errorText = 'The Contact Name and Number are blank';
		} else {
			errorText = 'The Contact Number is blank';
		}
	}

	return (
		<>
			{errorText && (
				<Row>
					<Col lg={12}>
						<Alert variant="danger-outline" className="mb-3">
							{errorText}, this should not happen and needs fixed in the TFN
							Registry immediately.
						</Alert>
					</Col>
				</Row>
			)}
			<CollapsibleCard header="Somos Data">
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="RespOrgId"
							value={queryProfile?.respOrgId}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Notes"
							value={queryProfile?.notes}
						/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Status"
							value={
								queryProfile?.tfnStatusId
									? tfnStatusToName(queryProfile.tfnStatusId)
									: ''
							}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Status Timestamp"
							value={
								queryProfile?.lastStatusTs
									? timestampToReadable(queryProfile.lastStatusTs)
									: ''
							}
						/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Contact Name"
							value={queryProfile?.contactName}
							color={
								!queryProfile?.contactName && queryProfile?.canManageRespOrg
									? 'warning'
									: 'primary'
							}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Contact Number"
							value={TollFreeNumbersUtils.tollFreeNumberToReadableString(
								queryProfile?.contactNumber || ''
							)}
							color={
								!queryProfile?.contactName && queryProfile?.canManageRespOrg
									? 'warning'
									: 'primary'
							}
						/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Trouble Name"
							value={troubleProfile?.summary}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Trouble Number"
							value={TollFreeNumbersUtils.tollFreeNumberToReadableString(
								troubleProfile?.troubleNumber || ''
							)}
						/>
					</Col>
				</Row>
			</CollapsibleCard>

			<CollapsibleCard header="RouteTrust Data">
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Status"
							value={
								profile.tfnStatusId ? tfnStatusToName(profile.tfnStatusId) : ''
							}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Updated"
							value={timestampToReadable(profile.lastUpdateTs)}
						/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Working"
							value={timestampToReadable(profile.lastWorkingTs)}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Status Timestamp"
							value={timestampToReadable(profile.statusTs)}
						/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Reserved"
							value={timestampToReadable(profile.lastReserveTs)}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Pending"
							value={timestampToReadable(profile.lastPendingTs)}
						/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Spare"
							value={timestampToReadable(profile.lastSpareTs)}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Transitional"
							value={timestampToReadable(profile.lastTransitionalTs)}
						/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Unavailable"
							value={timestampToReadable(profile.lastUnavailableTs)}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Working RespOrg"
							value={profile.lastWorkingRespOrgId}
						/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Last Working Days"
							value={
								profile.lastWorkingDays ? String(profile.lastWorkingDays) : ''
							}
						/>
					</Col>
					<Col md={6}>
						<RtxTextInput
							className="mb-3"
							label="Available Time"
							value={timestampToReadable(profile.availableTs)}
						/>
					</Col>
				</Row>
			</CollapsibleCard>
		</>
	);
};

AdminDataInfo.displayName = 'AdminDataInfo';
